import { ref, onMounted, onUnmounted, type Ref } from "vue";

export interface MenuBehaviorOptions {
  /**
   * The menu container element ref
   */
  menuContainerRef: Ref<HTMLElement | null>;
}

/**
 * Composable for menu behavior following Vue community best practices
 * Uses standard click-outside detection pattern similar to VueUse
 * Handles click-outside detection and escape key handling
 */
export function useMenuBehavior(options: MenuBehaviorOptions) {
  const { menuContainerRef } = options;
  const isOpen = ref(false);

  /**
   * Handle clicks outside the menu following Vue community standards
   * Based on VueUse onClickOutside pattern
   */
  function handleClickOutside(event: Event) {
    if (!isOpen.value) return;
    
    const target = event.target as HTMLElement;
    if (!menuContainerRef.value || !target) return;
    
    // Don't close if clicking within the menu container or its descendants
    if (menuContainerRef.value.contains(target) || menuContainerRef.value === target) {
      return;
    }
    
    // Close the menu
    isOpen.value = false;
  }

  /**
   * Handle escape key to close menu (accessibility requirement)
   */
  function handleEscapeKey(event: KeyboardEvent) {
    if (event.key === "Escape" && isOpen.value) {
      isOpen.value = false;
      event.preventDefault();
    }
  }

  /**
   * Open the menu
   */
  function openMenu() {
    isOpen.value = true;
  }

  /**
   * Close the menu
   */
  function closeMenu() {
    isOpen.value = false;
  }

  /**
   * Toggle menu state
   */
  function toggleMenu() {
    isOpen.value = !isOpen.value;
  }

  // Set up event listeners following Vue 3 lifecycle best practices
  onMounted(() => {
    // Use capture phase for better click detection
    document.addEventListener("click", handleClickOutside, true);
    document.addEventListener("keydown", handleEscapeKey);
  });

  // Clean up event listeners to prevent memory leaks
  onUnmounted(() => {
    document.removeEventListener("click", handleClickOutside, true);
    document.removeEventListener("keydown", handleEscapeKey);
  });

  return {
    isOpen,
    openMenu,
    closeMenu,
    toggleMenu,
  };
}